import React, { useEffect, useMemo, useState } from "react";
import "./admin-payment.css";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import { MdDelete } from "react-icons/md";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { deleteTransactionForAdmin, getTransactionListForAdmin } from "./../../api/transaction.api";
import { paymentMethodText, statusText } from "../../utils";
import ConfirmModal from "../../components/modal/confirm-modal";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminPayment = () => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);
  const [selectedTransactionStatusType, setSelectedTransactionStatusType] = useState("all");
  const [selectedTransactionType, setSelectedTransactionType] = useState("all");
  const [transactions, setTransactions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const { user } = useUser();

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchTransactionList();
    document.title = "Admin - Giao dịch nạp tiền";
  }, [currentPage, selectedTransactionStatusType, selectedTransactionType, debouncedSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const summary = useMemo(() => {
    return transactions.reduce(
      (acc, tx) => {
        if (tx.transactionStatus === "success") acc.success += 1;
        if (tx.transactionStatus === "pending") acc.pending += 1;
        if (tx.transactionStatus === "failed") acc.failed += 1;
        return acc;
      },
      { success: 0, pending: 0, failed: 0 }
    );
  }, [transactions]);

  const fetchTransactionList = async (showLoader = true) => {
    try {
      if (showLoader) setIsFetching(true);

      const res = await getTransactionListForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim(),
        selectedTransactionStatusType,
        selectedTransactionType
      );

      if (res.success) {
        setTransactions(res.data.transactions || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalTransactions(res.data.total || 0);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleDeleteTransaction = async () => {
    if (isActionLoading) return;
    try {
      setIsActionLoading(true);
      setIsModalOpen(false);
      const res = await deleteTransactionForAdmin(selectedIdToDelete);

      if (res.success) {
        showToast("Xóa giao dịch thành công", "success");
        setSelectedIdToDelete("");
        await fetchTransactionList(false);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="admin-payment-page">
      <ToastNotification />
      <div className="admin-payment-shell">
        <div className="admin-payment-header">
          <div>
            <p className="admin-payment-breadcrumb">Quản trị / Giao dịch nạp tiền</p>
            <h1 className="admin-payment-title">Danh sách giao dịch</h1>
          </div>
          <div className="admin-payment-kpi">
            <div className="admin-payment-kpi-item">
              <span>Tổng giao dịch</span>
              <strong>{totalTransactions}</strong>
            </div>
            <div className="admin-payment-kpi-item">
              <span>Trang hiện tại</span>
              <strong>{transactions.length}</strong>
            </div>
            <div className="admin-payment-kpi-item">
              <span>Đang chờ</span>
              <strong>{summary.pending}</strong>
            </div>
          </div>
        </div>

        <div className="admin-payment-toolbar">
          <div className="admin-payment-search-wrap">
            <FaSearch className="admin-payment-search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm theo mã giao dịch"
              className="admin-payment-search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            className="admin-payment-filter"
            value={selectedTransactionStatusType}
            onChange={(e) => {
              setSelectedTransactionStatusType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="success">Thành công</option>
            <option value="pending">Đang chờ</option>
            <option value="failed">Thất bại</option>
          </select>

          <select
            className="admin-payment-filter"
            value={selectedTransactionType}
            onChange={(e) => {
              setSelectedTransactionType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tất cả phương thức</option>
            <option value="bank_transfer">Ngân hàng</option>
            <option value="card">Thẻ cào</option>
          </select>
        </div>

        {isFetching && transactions.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="admin-payment-table-wrap">
              <table className="admin-payment-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Mã giao dịch</th>
                    <th>Tên người dùng</th>
                    <th>Mệnh giá</th>
                    <th>Phương thức</th>
                    <th>Trạng thái</th>
                    <th>Cổng thanh toán</th>
                    <th>Thông tin</th>
                    <th>Thời gian</th>
                    {hasPermission(user?.role, "delete:payment") && <th>Thao tác</th>}
                  </tr>
                </thead>
                <tbody>
                  {transactions.length > 0 ? (
                    transactions.map((tx, index) => (
                      <tr key={tx.transactionId}>
                        <td className="admin-payment-cell-muted">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td className="admin-payment-cell-code">{tx.transactionId}</td>
                        <td>
                          <a className="admin-payment-user-link" href={`/super-admin/users/view/${tx.userId?._id}`}>
                            {tx.userId?.username}
                          </a>
                        </td>
                        <td className="admin-payment-cell-price">{tx.amount?.toLocaleString("vi-VN") || 0}đ</td>
                        <td>{paymentMethodText(tx.paymentMethod)}</td>
                        <td>
                          <span className={`admin-payment-status admin-payment-status-${tx.transactionStatus}`}>
                            {statusText(tx.transactionStatus)}
                          </span>
                        </td>
                        <td>{tx.gateway === "empty_gateway" ? "NA" : tx.gateway}</td>
                        <td className="admin-payment-cell-muted">
                          {tx?.transaction_attributes ? (
                            <>
                              <p>Serial: {tx.transaction_attributes.serial || "-"}</p>
                              <p>Code: {tx.transaction_attributes.code || "-"}</p>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="admin-payment-cell-muted">{new Date(tx.createdAt).toLocaleString("vi-VN")}</td>
                        {hasPermission(user?.role, "delete:payment") && (
                          <td>
                            <div className="admin-payment-actions">
                              <button
                                onClick={() => {
                                  setIsModalOpen(true);
                                  setSelectedIdToDelete(tx.transactionId);
                                }}
                                className="admin-payment-action admin-payment-action-delete"
                                disabled={isActionLoading}
                                title="Xóa giao dịch"
                              >
                                <MdDelete />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={hasPermission(user?.role, "delete:payment") ? 10 : 9}>
                        <p className="admin-payment-empty">Không có giao dịch phù hợp bộ lọc.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={handleDeleteTransaction}
        onClose={() => setIsModalOpen(false)}
        message={"Xác nhận bạn đang xóa một giao dịch"}
        title={"Xóa giao dịch"}
      />
    </div>
  );
};
